import {Graphic} from '../Graphic';
import {SubscriptionManager} from '@/lib/filters/subscriptions';
import {gmAPI, MapService, Markers} from '@/services/MapService';
import {geohashForLocation} from 'geofire-common';

/**
 * @typedef {import('../GraphicService').GraphicData} PlaceMarkerGraphicData
 */

class PlaceMarkerGraphic extends Graphic {
  /**
   * @param {PlaceMarkerGraphicData} data
   */
  constructor(data) {
    super();
    /** @type {string} */
    this.icon = data.icon ?? 'Blue-Balloon-Flat';
    /**
     * @type {google.maps.LatLng|google.maps.LatLngLiteral} position for the center of the placemarker.
     */
    this.position = data.bounds ? MapService.calculateCenter(data.bounds) : data.position;
    /** @type {boolean} */
    this.clickable = true;
    /** @type {SubscriptionManager} */
    this.subscriptions = new SubscriptionManager();
    /** @type {string} Document ID of the feature in Firestore */
    this.id = data.id;
    /** @type {string} Collection the marker belongs to. Determinesthe z-index of the different marker types */
    this.collection = 'features';
    /** @type {string} Name of the marker */
    this.name = data.title ?? '';
    /** @type {Array<{event: string, callback: Function}>} */
    this.events = getEventsForPlaceMarkerGraphic(this);

    /** @type {import('@/services/MapService/Markers').MarkerData} */
    this.marker = Markers.create({
      type: data.featureType,
      collection: 'features',
      id: this.id,
      name: this.name,
      latLng: {
        lat: this.position?.lat() || 0,
        lng: this.position?.lng() || 0,
      },
      icon: this.icon,
      events: this.events,
    });
  }

  /**
   * Adds a new event handler to this place marker.
   * @param {string} eventType The type of event for which to invoke the handler.
   * @param {string} handlerName A name identifying this handler for later removal.
   * @param {Function} handler The handler to be invoked.
   */
  addHandler(eventType, handlerName, handler) {
    if (!this.marker) return;
    // Placemarkers require a local listener on click events instead of using the Event Service
    Markers.addListener(this.collection, this.id, eventType, handler);
  }

  /**
   * Adds a local event handler to this place marker.
   * @param {string} eventType The type of event for which to invoke the handler.
   * @param {string} handlerName A name identifying this handler for later removal.
   * @param {Function} handler The handler to be invoked.
   */
  addLocalHandler(eventType, handlerName, handler) {
    if (!this.marker) return;
    Markers.addListener(this.collection, this.id, eventType, handler);
  }

  /** @param {boolean} enabled */
  setPointerEvents(enabled) {
    Markers.setAllPickable(enabled);
  }

  removeLocalHandler(eventType, handlerName, handler) {
    if (!this.marker) return;
    Markers.removeListener(this.collection, this.id, eventType, handler);
  }

  /**
   * Sets whether the markers can be clicked
   * @param {boolean} clickable Whether the markers can be clicked
   */
  setClickable(clickable) {
    this.clickable = clickable;
  }

  /**
   * @return {google.maps.LatLng} The position of the marker
   */
  getPosition() {
    return this.position;
  }

  /**
   * @return {string} The geohash of the marker
   */
  getGeohash() {
    return this.geohash;
  }

  /**
   * Sets the position of the marker and updates its geohash
   * @param {google.maps.LatLng} position The position of the marker
   */
  setPosition(position) {
    this.position = position;
    this.geohash = geohashForLocation([position.lat(), position.lng()]);
  }

  /**
   * Retrieves the icon value
   * @return {string} Gives the icon retrieve value
   */
  getIcon() {
    return this.icon;
  }

  /**
   * Sets the icon to use for the markers for returned places of interest
   * @param {string} icon The icon to use for the markers for returned places of interest
   */
  setIcon(icon) {
    this.icon = icon;
    Markers.updateMarker(this.collection, this.id, {icon});
    this.subscriptions.publish('graphic_changed');
  }

  isClickable() {
    return this.clickable;
  }

  /**
   * @param {boolean} editing The new editing state
   * @param {Object} context Additional context for the editing state
   * @return {Promise<void>}
   */
  async setEditing(editing, context) {
    if (editing) {
      this.subscriptions.publish('open_info_window', {
        toolbar: this.getToolbarOptions(),
        location: this.getPosition(),
        openInEditMode: context?.openInEditMode ?? false,
      });
      return;
    }
    return;
  }

  subscribe(eventName, callback) {
    return this.subscriptions.addSubscription(eventName, callback);
  }

  getToolbarOptions() {
    return {
      toolbarOptions: [
        [
          {
            icon: 'Fill',
            action: 'iconPicker',
            name: 'Change marker icon',
            pickers: [
              {
                title: 'Icon Picker',
                getIcon: () => this.getIcon(),
                setIcon: (icon) => this.setIcon(icon),
              },
            ],
          },
          {
            icon: 'ContentCopy',
            action: 'duplicateMarker',
            name: 'Duplicate Comp',
            callback: () => this.subscriptions.publish('duplicate_feature', {
              featureId: this.id,
            }),
          },
        ],
      ],
    };
  }

  /**
   * Sets the visibility of the placemarker
   * @param {google.maps.Map|null} map
   * @return {void}
   */
  setMap(map) {
    const visible = (map !== null);
    Markers.setVisible(this.collection, [this.id], visible);
  }

  /* Markers are always displayed over other features. */
  setZIndex(zIndex) {}

  /**
   * Converts the PlaceMarkerGraphic to a JSON object
   * @return {PlaceMarkerGraphicData}
   */
  toJSON() {
    const superJSON = super.toJSON();
    const northSouth = this.position?.lat();
    const eastWest = this.position?.lng();
    const bounds = {north: northSouth, south: northSouth, east: eastWest, west: eastWest};

    return {
      ...superJSON,
      geohash: geohashForLocation([northSouth, eastWest]),
      bounds,
      graphicData: {
        bounds,
        featureType: 'placeMarker',
        icon: this.icon,
      },
    };
  }

  /**
   * Sets the highlight state of the placemarker
   * @param {boolean} state The highlight state of the placemarker
   */
  setHighlight(state) {
    Markers.setHighlight(this.collection, this.id, state);
  }

  /**
   * Triggers the bounce animation for the placemarker
   */
  displayLocation() {
    if (this.marker == null) {
      throw new Error('place marker graphic marker is null!');
    }

    Markers.playBounceAnimation(this.collection, this.id);
  }

  /**
   * Retrieves the location of the placemarker
   * @return {google.maps.LatLngLiteral} The location of the placemarker
   */
  getLocation() {
    return {lat: this.position.lat(), lng: this.position.lng()};
  }
}

/**
 * Returns the events for the place marker graphic.
 * @param {PlaceMarkerGraphic} graphic The place marker graphic
 * @return {Array<{event: string, callback: Function}>} The events for the place marker graphic
 */
function getEventsForPlaceMarkerGraphic(graphic) {
  const dragEndEvent = {
    event: 'dragend',
    callback: (latLng) => {
      // Update current graphic's position and persist to Firestore
      graphic.position = new gmAPI.LatLng(latLng);
      graphic.subscriptions.publish('graphic_changed');
    },
  };

  return [dragEndEvent];
}

/**
 * Creates a PlaceMarkerGraphic.
 * @param {PlaceMarkerGraphicData} data
 * @return {PlaceMarkerGraphic}
 */
function createPlaceMarkerGraphic(data) {
  /** @type {PlaceMarkerGraphic} */
  let graphic;
  try {
    graphic = new PlaceMarkerGraphic(data);
  } catch (err) {
    throw new Error(`Error creating PlaceMarkerGraphic: ${err.message}`);
  }
  return graphic;
}

export {PlaceMarkerGraphic, createPlaceMarkerGraphic};
